import { ComponentType, useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ThermometerIcon,
  ZapIcon,
  WindIcon,
  ChevronRightIcon,
  PersonStandingIcon,
  DropletsIcon,
  CheckSquareIcon,
  SquareIcon,
  PowerIcon,
  Loader2Icon,
  CheckIcon,
  ChevronsUpDownIcon
} from 'lucide-react';
import {
  getSensorIcon,
  getSensorColorObject,
  getSensorTypeName,
  SensorType
} from '../../utils/sensorUtils';
import { TelemetryData } from '../../hooks/useTelemetry';
import { thingsboardService } from '../../services/thingsboard';

export interface Sensor {
  id: string;
  name?: string;
  type: SensorType;
  label: string;
  alias?: string;
  roomId?: string;
  active: boolean;
  x: number;
  y: number;
  primary_appliance?: string;
  appliance_names?: string;
}

export interface Room {
  id: string;
  name: string;
  temp: number;
  humidity: number;
  co2: number;
  tvoc: number;
  dust: number;
  energy: number;
  icon: ComponentType<{
    className?: string;
  }>;
}

interface SensorListViewProps {
  room?: Room;
  sensors: Sensor[];
  telemetry: Record<string, TelemetryData>;
  onSelectSensor: (sensor: Sensor) => void;
  onShowToast?: (message: string, type?: 'success' | 'info' | 'error') => void;
  hideSummaryCards?: boolean;
  embedded?: boolean;
}

// ─── Constants ───
const DEVICE_TABS = [
  { id: 'ALL', label: '전체' },
  { id: 'SEM', label: '멀티탭' },
  { id: 'SEO', label: '콘센트' },
  { id: 'SES', label: '스위치' },
  { id: 'SEL', label: '조명' },
  { id: 'SER', label: '릴레이' },
  { id: 'ETC', label: '기타' }
] as const;

type TabId = typeof DEVICE_TABS[number]['id'];

const TYPE_PRIORITY: Record<string, number> = {
  'SEM': 1,
  'SEO': 2,
  'SER': 3,
  'SES': 4,
  'SEL': 5,
  'SEC': 6,
  'RADAR': 7,
};


/**
 * Format ERV telemetry value:
 * 0: 정지, 1: 약풍, 2: 중풍, 3: 강풍, -1: 실패
 */
export function formatErvStatus(rawVal: any): { text: string; color: string } | null {
  if (rawVal === undefined || rawVal === null || rawVal === '') return null;
  const num = Number(rawVal);
  if (isNaN(num)) {
    const str = String(rawVal).trim();
    if (str === '0' || str === '정지' || str === 'off' || str === 'stop') return { text: '정지', color: 'text-slate-500' };
    if (str === '1' || str === '약풍' || str === 'low') return { text: '약풍', color: 'text-emerald-400' };
    if (str === '2' || str === '중풍' || str === 'medium' || str === 'mid') return { text: '중풍', color: 'text-teal-400' };
    if (str === '3' || str === '강풍' || str === 'high') return { text: '강풍', color: 'text-cyan-400' };
    if (str === '-1' || str === '실패' || str === 'error' || str === 'fail') return { text: '실패', color: 'text-rose-400' };
    return { text: str, color: 'text-emerald-400' };
  }
  switch (num) {
    case 0:
      return { text: '정지', color: 'text-slate-500' };
    case 1:
      return { text: '약풍', color: 'text-emerald-400' };
    case 2:
      return { text: '중풍', color: 'text-teal-400' };
    case 3:
      return { text: '강풍', color: 'text-cyan-400' };
    case -1:
      return { text: '실패', color: 'text-rose-400' };
    default:
      return { text: `${num}`, color: 'text-emerald-400' };
  }
}

// ─── Friendly Device Names ───
export function getFriendlyDeviceTypeName(type: string): string {
  switch (type?.toUpperCase()) {
    case 'SEM': return '멀티탭';
    case 'SEO': return '콘센트';
    case 'SES': return '스위치';
    case 'SEL': return '조명';
    case 'SER': return '릴레이';
    case 'RADAR': return '재실센서';
    case 'SEC': return '커튼/블라인드';
    case 'CURTAIN': return '커튼';
    case 'BLIND': return '블라인드';
    case 'HERV':
    case 'SEV': return '전열교환기';
    default: return type || '기기';
  }
}

const naturalSort = (a: any, b: any) => {
  if (typeof a !== 'string' || typeof b !== 'string') return 0;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

// ─── Scoring helpers ───
function scoreTemp(temp: number): number {
  if (temp >= 22 && temp <= 26) return 100;
  if ((temp >= 20 && temp < 22) || (temp > 26 && temp <= 28)) return 70;
  if ((temp >= 18 && temp < 20) || (temp > 28 && temp <= 30)) return 40;
  return 10;
}

function scoreHumidity(h: number): number {
  if (h >= 40 && h <= 60) return 100;
  if ((h >= 30 && h < 40) || (h > 60 && h <= 70)) return 70;
  if ((h >= 20 && h < 30) || (h > 70 && h <= 80)) return 40;
  return 10;
}

function scoreCO2(ppm: number): number {
  if (ppm < 800) return 100;
  if (ppm < 1200) return 70;
  if (ppm < 2000) return 40;
  return 10;
}

function scoreTVOC(mgm3: number): number {
  if (mgm3 < 0.3) return 100;
  if (mgm3 < 0.6) return 70;
  if (mgm3 < 1.0) return 40;
  return 10;
}

function scorePM25(ugm3: number): number {
  if (ugm3 < 15) return 100;
  if (ugm3 < 35) return 70;
  if (ugm3 < 75) return 40;
  return 10;
}

function getAQIGrade(score: number) {
  if (score >= 80) return { label: '좋음', color: 'text-emerald-400', bgColor: 'bg-emerald-500/20' };
  if (score >= 60) return { label: '보통', color: 'text-cyan-400', bgColor: 'bg-cyan-500/20' };
  if (score >= 40) return { label: '나쁨', color: 'text-amber-400', bgColor: 'bg-amber-500/20' };
  return { label: '매우 나쁨', color: 'text-red-400', bgColor: 'bg-red-500/20' };
}

function getEnergyGrade(power: number) {
  if (power < 1) return { label: '낮음', color: 'text-emerald-400', bgColor: 'bg-emerald-500/20' };
  if (power < 5) return { label: '보통', color: 'text-cyan-400', bgColor: 'bg-cyan-500/20' };
  if (power < 10) return { label: '높음', color: 'text-amber-400', bgColor: 'bg-amber-500/20' };
  return { label: '매우 높음', color: 'text-red-400', bgColor: 'bg-red-500/20' };
}

function getComfortScoreLabel(temp: number, humidity: number) {
  const ts = scoreTemp(temp);
  const hs = scoreHumidity(humidity);
  const avg = (ts + hs) / 2;
  if (avg >= 80) return { label: '쾌적', color: 'text-emerald-400' };
  if (avg >= 60) return { label: '양호', color: 'text-cyan-400' };
  if (avg >= 40) return { label: '보통', color: 'text-amber-400' };
  return { label: '불쾌', color: 'text-red-400' };
}

// ─── Component ───
export function SensorListView({
  room,
  sensors,
  telemetry,
  onSelectSensor,
  onShowToast,
  hideSummaryCards = true,
  embedded = false
}: SensorListViewProps) {
  const [selectedTab, setSelectedTab] = useState<TabId>('ALL');
  const [viewLayout, setViewLayout] = useState<'list' | 'grid'>('grid');
  const [sortMode, setSortMode] = useState<'type' | 'name'>('type');
  const [selectedSensorIds, setSelectedSensorIds] = useState<Set<string>>(new Set());
  const [isControlling, setIsControlling] = useState(false);
  const [onlyOnline, setOnlyOnline] = useState<boolean>(false);
  const [localPowerStates, setLocalPowerStates] = useState<Record<string, boolean>>({});

  // Tab counts
  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = { ALL: sensors.length, ETC: 0 };
    sensors.forEach((s) => {
      const t = s.type?.toUpperCase() || '';
      if (['SEM', 'SEO', 'SES', 'SEL', 'SER'].includes(t)) {
        counts[t] = (counts[t] || 0) + 1;
      } else {
        counts.ETC = (counts.ETC || 0) + 1;
      }
    });
    return counts;
  }, [sensors]);

  // Filtered by active tab
  const filteredSensors = useMemo(() => {
    return sensors.filter((sensor) => {
      if (onlyOnline && !sensor.active) return false;
      const t = sensor.type?.toUpperCase() || '';
      if (selectedTab === 'ALL') return true;
      if (selectedTab === 'ETC') {
        return !['SEM', 'SEO', 'SES', 'SEL', 'SER'].includes(t);
      }
      return t === selectedTab;
    });
  }, [sensors, selectedTab, onlyOnline]);

  // Sorted sensors (Defaults to 'name' when a specific category tab is selected)
  const sortedSensors = useMemo(() => {
    return [...filteredSensors].sort((a, b) => {
      const effectiveSortMode = selectedTab === 'ALL' ? sortMode : 'name';
      if (effectiveSortMode === 'type') {
        const priorityA = TYPE_PRIORITY[a.type?.toUpperCase()] || 99;
        const priorityB = TYPE_PRIORITY[b.type?.toUpperCase()] || 99;
        if (priorityA !== priorityB) return priorityA - priorityB;
        if (a.type !== b.type) return naturalSort(a.type, b.type);
        return naturalSort(a.label, b.label);
      } else {
        return naturalSort(a.label, b.label);
      }
    });
  }, [filteredSensors, sortMode, selectedTab]);

  // Selection handlers
  const isAllSelected = sortedSensors.length > 0 && sortedSensors.every(s => selectedSensorIds.has(s.id));

  const toggleSelectAll = useCallback(() => {
    setSelectedSensorIds(prev => {
      const next = new Set(prev);
      if (isAllSelected) {
        // Deselect all visible
        sortedSensors.forEach(s => next.delete(s.id));
      } else {
        // Select all visible
        sortedSensors.forEach(s => next.add(s.id));
      }
      return next;
    });
  }, [isAllSelected, sortedSensors]);

  const toggleSelectSensor = useCallback((id: string, e?: React.MouseEvent) => {
    e?.stopPropagation?.();
    setSelectedSensorIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Helper to check if a sensor is in Normal (일반) mode
  const isSensorInNormalMode = useCallback((sensor: Sensor): boolean => {
    const t = sensor.type?.toUpperCase();
    const sData = telemetry[sensor.id] || (sensor as any).telemetry || {};
    if (t === 'SEO' || t === 'SEM' || t === 'SER') {
      const rawMode = sData.plug_mode?.[0]?.value ?? sData.mode?.[0]?.value ?? sData.seo_mode?.[0]?.value ?? (sensor as any).mode;
      const modeNum = Number(rawMode);
      return rawMode === 'normal' || modeNum === 4 || String(rawMode) === '4';
    }
    if (t === 'SEL') {
      const rawSelMode = sData.sel_mode?.[0]?.value ?? sData.mode?.[0]?.value ?? (sensor as any).mode;
      return rawSelMode === 'normal' || String(rawSelMode) === 'normal' || !rawSelMode;
    }
    if (t === 'SES') {
      return true;
    }
    return false;
  }, [telemetry]);

  // Batch control handler (Turn ON / Turn OFF for Active & Normal Mode devices only)
  const handleBatchControl = async (turnOn: boolean) => {
    const totalSelectedCount = selectedSensorIds.size;
    if (totalSelectedCount === 0) return;
    setIsControlling(true);

    // Target devices MUST be: (1) Active (Online) AND (2) in Normal (일반) Mode
    const targetSensors = sensors.filter(s => {
      if (!selectedSensorIds.has(s.id)) return false;
      const isActive = s.active === true || String(s.active) === 'true';
      if (!isActive) return false;
      return isSensorInNormalMode(s);
    });
    
    if (targetSensors.length === 0) {
      if (onShowToast) {
        onShowToast(`${totalSelectedCount}개 기기 중 0개 ${turnOn ? '켜기' : '끄기'} 완료 (온라인 & 일반 모드 기기 없음)`, 'info');
      }
      setSelectedSensorIds(new Set());
      setIsControlling(false);
      return;
    }

    const commandVal = turnOn ? 'on' : 'off';

    // Optimistic UI update for all valid target devices
    setLocalPowerStates(prev => {
      const next = { ...prev };
      targetSensors.forEach(s => {
        next[s.id] = turnOn;
      });
      return next;
    });

    let successCount = 0;
    let failCount = 0;

    await Promise.allSettled(
      targetSensors.map(async (sensor) => {
        const type = sensor.type?.toUpperCase();
        try {
          if (type === 'SEO' || type === 'SEM') {
            await thingsboardService.sendOneWayRpc(sensor.id, 'set_plug', { value: commandVal });
            setTimeout(() => {
              thingsboardService.sendOneWayRpc(sensor.id, 'set_plug', { value: commandVal }).catch(() => {});
            }, 100);
            successCount++;
          } else if (type === 'SES') {
            await thingsboardService.sendOneWayRpc(sensor.id, 'set_switch', { value: commandVal });
            setTimeout(() => {
              thingsboardService.sendOneWayRpc(sensor.id, 'set_switch', { value: commandVal }).catch(() => {});
            }, 100);
            successCount++;
          } else if (type === 'SER') {
            await thingsboardService.sendOneWayRpc(sensor.id, 'set_relay', { value: commandVal })
              .catch(() => thingsboardService.sendOneWayRpc(sensor.id, 'set_plug', { value: commandVal }));
            setTimeout(() => {
              thingsboardService.sendOneWayRpc(sensor.id, 'set_relay', { value: commandVal })
                .catch(() => thingsboardService.sendOneWayRpc(sensor.id, 'set_plug', { value: commandVal }));
            }, 100);
            successCount++;
          } else if (type === 'SEL') {
            await thingsboardService.sendOneWayRpc(sensor.id, 'set_light', { value: commandVal })
              .catch(() => thingsboardService.sendOneWayRpc(sensor.id, 'set_switch', { value: commandVal }));
            setTimeout(() => {
              thingsboardService.sendOneWayRpc(sensor.id, 'set_light', { value: commandVal })
                .catch(() => thingsboardService.sendOneWayRpc(sensor.id, 'set_switch', { value: commandVal }));
            }, 100);
            successCount++;
          }
        } catch (err) {
          console.error(`Failed to batch control device ${sensor.id}:`, err);
          failCount++;
        }
      })
    );

    setIsControlling(false);
    setSelectedSensorIds(new Set()); // Automatically clear selection after control!

    const msg = `${totalSelectedCount}개 기기 중 ${successCount}개 ${turnOn ? '켜기' : '끄기'} 완료` + (failCount > 0 ? ` (${failCount}개 실패)` : '');
    if (onShowToast) {
      onShowToast(msg, successCount > 0 ? 'success' : 'info');
    }
  };

  // Curtain toggle / open controller handler
  const handleToggleCurtain = (sensor: Sensor, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!sensor.active) {
      if (onShowToast) onShowToast('오프라인(비활성) 상태의 기기는 제어할 수 없습니다.', 'info');
      return;
    }
    window.dispatchEvent(new CustomEvent('device-toast', {
      detail: { name: sensor.label, alias: sensor.alias }
    }));
  };

  // Single device power toggle handler with optimistic UI and retry
  const handleToggleSinglePower = async (sensor: Sensor, isCurrentlyOn: boolean, isAlways: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!sensor.active) {
      if (onShowToast) onShowToast('기기가 연결되어 있지 않습니다.', 'error');
      return;
    }
    if (isAlways) {
      if (onShowToast) onShowToast('상시전원 모드 기기는 전원을 끌 수 없습니다.', 'info');
      return;
    }

    const nextState = !isCurrentlyOn;
    const commandVal = nextState ? 'on' : 'off';
    const type = sensor.type?.toUpperCase();

    // Optimistic UI state update
    setLocalPowerStates(prev => ({ ...prev, [sensor.id]: nextState }));

    const sendRpc = async () => {
      if (type === 'SEO' || type === 'SEM') {
        await thingsboardService.sendOneWayRpc(sensor.id, 'set_plug', { value: commandVal });
      } else if (type === 'SES') {
        await thingsboardService.sendOneWayRpc(sensor.id, 'set_switch', { value: commandVal });
      } else if (type === 'SER') {
        await thingsboardService.sendOneWayRpc(sensor.id, 'set_relay', { value: commandVal })
          .catch(() => thingsboardService.sendOneWayRpc(sensor.id, 'set_plug', { value: commandVal }));
      } else if (type === 'SEL') {
        await thingsboardService.sendOneWayRpc(sensor.id, 'set_light', { value: commandVal })
          .catch(() => thingsboardService.sendOneWayRpc(sensor.id, 'set_switch', { value: commandVal }));
      }
    };

    try {
      await sendRpc();
      setTimeout(() => {
        sendRpc().catch(() => {});
      }, 100);
      if (onShowToast) {
        onShowToast(`${sensor.label} 전원을 ${nextState ? '켰습니다' : '껐습니다'}.`, 'success');
      }
    } catch (err) {
      console.error(`Failed to toggle device ${sensor.id}:`, err);
      // Rollback optimistic state
      setLocalPowerStates(prev => ({ ...prev, [sensor.id]: isCurrentlyOn }));
      if (onShowToast) onShowToast('전원 제어에 실패했습니다.', 'error');
    }
  };

  const safeRoom = room || {
    id: 'ALL',
    name: '공간',
    temp: 24,
    humidity: 48,
    energy: 0,
    co2: 450,
    tvoc: 0.1,
    dust: 10,
    icon: ThermometerIcon,
  };

  const roomTemp = typeof safeRoom.temp === 'number' && !isNaN(safeRoom.temp) ? safeRoom.temp : 24;
  const roomHumidity = typeof safeRoom.humidity === 'number' && !isNaN(safeRoom.humidity) ? safeRoom.humidity : 48;
  const roomEnergy = typeof safeRoom.energy === 'number' && !isNaN(safeRoom.energy) ? safeRoom.energy : 0;
  const energyState = getEnergyGrade(roomEnergy);
  const comfortInfo = getComfortScoreLabel(roomTemp, roomHumidity);
  const co2Score = scoreCO2(safeRoom.co2 || 450);
  const tvocScore = scoreTVOC(safeRoom.tvoc || 0.1);
  const pm25Score = scorePM25(safeRoom.dust || 10);
  const overallAQI = Math.round((co2Score + tvocScore + pm25Score) / 3);
  const aqiGrade = getAQIGrade(overallAQI);

  return (
    <div
      className={embedded ? "w-full space-y-3" : "flex-1 overflow-y-auto px-4 pt-3 pb-24 space-y-3 overscroll-contain"}
      style={embedded ? {} : { background: 'var(--mobile-bg-alt)' }}
    >
      {/* ─── 2. Device Tabs Navigation (Horizontal Slide / Swipe UI) ─── */}
      <div className="relative -mx-4 px-4 py-1">
        <div
          className="flex items-center gap-2 overflow-x-auto select-none py-1 px-1 overscroll-x-contain"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {DEVICE_TABS.filter((tab) => tab.id === 'ALL' || (tabCounts[tab.id] ?? 0) > 0).map((tab) => {
            const count = tabCounts[tab.id] ?? 0;
            const isActive = selectedTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={(e) => {
                  setSelectedTab(tab.id);
                  e.currentTarget.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'center',
                    block: 'nearest'
                  });
                }}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all duration-200 ${
                  isActive
                    ? 'bg-cyan-500 text-black shadow-lg shadow-cyan-500/25 font-bold scale-[1.02]'
                    : 'border active:scale-95'
                }`}
                style={
                  isActive
                    ? {}
                    : {
                        backgroundColor: 'var(--mobile-bg-sub)',
                        borderColor: 'var(--mobile-border)',
                        color: 'var(--mobile-text-secondary)'
                      }
                }
              >
                <span>{tab.label}</span>
                <span
                  className={`text-[0.68rem] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                    isActive ? 'bg-black/20 text-black' : 'bg-white/10 text-cyan-400'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── 3. Toolbar: View Mode, Sort, Select All, Batch Actions ─── */}
      <div className="flex flex-col gap-2 pt-1">
        {/* Top bar */}
        <div className="flex items-center justify-between px-1">
          {/* Left: Device Count & Select All */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium border transition-colors"
              style={{
                backgroundColor: isAllSelected ? 'rgba(6, 182, 212, 0.15)' : 'var(--mobile-bg-sub)',
                borderColor: isAllSelected ? 'rgba(6, 182, 212, 0.4)' : 'var(--mobile-border)',
                color: isAllSelected ? '#22d3ee' : 'var(--mobile-text-secondary)'
              }}
            >
              {isAllSelected ? (
                <CheckSquareIcon className="w-3.5 h-3.5 text-cyan-400" />
              ) : (
                <SquareIcon className="w-3.5 h-3.5 text-slate-500" />
              )}
              <span>전체선택</span>
              {selectedSensorIds.size > 0 && (
                <span className="text-[0.7rem] font-bold text-cyan-400">
                  ({selectedSensorIds.size})
                </span>
              )}
            </button>
          </div>

          {/* Right: Sort Mode + Online Only Checkbox */}
          <div className="flex items-center gap-2">
            {/* Sort Mode (Only visible when 'ALL' tab is selected) */}
            {selectedTab === 'ALL' && (
              <div className="flex rounded-lg p-0.5 border" style={{ background: 'var(--mobile-bg-sub)', borderColor: 'var(--mobile-border)' }}>
                <button
                  onClick={() => setSortMode('type')}
                  className={`px-2 py-0.5 rounded-md text-[0.7rem] font-medium transition-all ${
                    sortMode === 'type' ? 'bg-cyan-500/20 text-cyan-400 font-bold' : 'text-slate-400'
                  }`}
                >
                  종류순
                </button>
                <button
                  onClick={() => setSortMode('name')}
                  className={`px-2 py-0.5 rounded-md text-[0.7rem] font-medium transition-all ${
                    sortMode === 'name' ? 'bg-cyan-500/20 text-cyan-400 font-bold' : 'text-slate-400'
                  }`}
                >
                  이름순
                </button>
              </div>
            )}

            {/* Online Only Checkbox (온라인만 보기) */}
            <button
              onClick={() => setOnlyOnline(!onlyOnline)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors select-none"
              style={{
                backgroundColor: onlyOnline ? 'rgba(52, 211, 153, 0.15)' : 'var(--mobile-bg-sub)',
                borderColor: onlyOnline ? 'rgba(52, 211, 153, 0.4)' : 'var(--mobile-border)',
                color: onlyOnline ? '#34d399' : 'var(--mobile-text-secondary)'
              }}
              title="온라인 디바이스만 필터링"
            >
              {onlyOnline ? (
                <CheckSquareIcon className="w-3.5 h-3.5 text-emerald-400" />
              ) : (
                <SquareIcon className="w-3.5 h-3.5 text-slate-500" />
              )}
              <span className="flex items-center gap-1">
                <span className={`w-1.5 h-1.5 rounded-full ${onlyOnline ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]' : 'bg-slate-500'}`} />
                온라인만
              </span>
            </button>
          </div>
        </div>

        {/* Batch Action Buttons Bar (Always accessible or when selected) */}
        <div className="flex items-center gap-2">
          <button
            disabled={selectedSensorIds.size === 0 || isControlling}
            onClick={() => handleBatchControl(true)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-bold border transition-all ${
              selectedSensorIds.size > 0 && !isControlling
                ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400 active:scale-95 shadow-sm'
                : 'opacity-40 cursor-not-allowed border-white/5 bg-white/5 text-slate-500'
            }`}
          >
            {isControlling ? (
              <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <PowerIcon className="w-3.5 h-3.5 text-emerald-400" />
            )}
            <span>선택 켜기</span>
            {selectedSensorIds.size > 0 && <span>({selectedSensorIds.size})</span>}
          </button>

          <button
            disabled={selectedSensorIds.size === 0 || isControlling}
            onClick={() => handleBatchControl(false)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-bold border transition-all ${
              selectedSensorIds.size > 0 && !isControlling
                ? 'bg-red-500/15 border-red-500/30 text-red-400 active:scale-95 shadow-sm'
                : 'opacity-40 cursor-not-allowed border-white/5 bg-white/5 text-slate-500'
            }`}
          >
            {isControlling ? (
              <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <PowerIcon className="w-3.5 h-3.5 text-red-400" />
            )}
            <span>선택 끄기</span>
            {selectedSensorIds.size > 0 && <span>({selectedSensorIds.size})</span>}
          </button>
        </div>
      </div>

      {/* ─── 4. Sensor Cards List / Grid ─── */}
      <div>
        {sortedSensors.length === 0 ? (
          <div className="text-center py-16 text-xs" style={{ color: 'var(--mobile-text-muted)' }}>
            해당 탭에 등록된 디바이스가 없습니다
          </div>
        ) : (
          /* ─── 2열 그리드 레이아웃 ─── */
          <div className="grid grid-cols-2 gap-2.5">
            {sortedSensors.map((sensor, i) => {
              const Icon = getSensorIcon(sensor.type);
              const colors = getSensorColorObject(sensor.type);
              const deviceData = telemetry[sensor.id] || {};

              const powerVal = deviceData.power_instant?.[0]?.value;
              const tempVal = deviceData.temperature?.[0]?.value;
              const humVal = deviceData.humidity?.[0]?.value;
              const presVal = deviceData.presence_state?.[0]?.value;
              const lightVal = deviceData.light_state?.[0]?.value;
              const ervVal = deviceData.erv?.[0]?.value ?? deviceData.ERV?.[0]?.value;
              const isSelected = selectedSensorIds.has(sensor.id);

              const isControllableType = ['SEO', 'SEM', 'SES', 'SER'].includes(sensor.type?.toUpperCase());
              const isPlugOrRelay = ['SEO', 'SEM', 'SER'].includes(sensor.type?.toUpperCase());
              const isCurtainType = ['SEC', 'CURTAIN', 'BLIND'].includes(sensor.type?.toUpperCase());
              
              const rawMode = deviceData.plug_mode?.[0]?.value ?? deviceData.mode?.[0]?.value ?? deviceData.seo_mode?.[0]?.value ?? (sensor as any).mode;
              const modeNum = Number(rawMode);
              const isOccupancyMode = isPlugOrRelay && (rawMode === 'occupancy' || modeNum === 1 || String(rawMode) === '1');
              const isAlwaysMode = isPlugOrRelay && (rawMode === 'always' || modeNum === 2 || String(rawMode) === '2');
              const isAiMode = isPlugOrRelay && (rawMode === 'ai' || modeNum === 3 || String(rawMode) === '3');
              const isNormalMode = isPlugOrRelay && (rawMode === 'normal' || modeNum === 4 || String(rawMode) === '4');
              const modeLabel = isAlwaysMode ? '상시' : isAiMode ? 'AI' : isNormalMode ? '일반' : '재실';
              
              const rawPlugState = deviceData.plug_on?.[0]?.value 
                ?? deviceData.switch?.[0]?.value 
                ?? deviceData.light_state?.[0]?.value 
                ?? deviceData.state?.[0]?.value;
              const serverPowerOn = rawPlugState !== undefined
                ? (String(rawPlugState) === 'true' || String(rawPlugState) === 'on' || String(rawPlugState) === '1')
                : false;
              const isPowerOn = localPowerStates[sensor.id] !== undefined ? localPowerStates[sensor.id] : serverPowerOn;

              return (
                <div
                  key={sensor.id}
                  onClick={() => onSelectSensor(sensor)}
                  className={`p-3 rounded-2xl cursor-pointer transition-all duration-200 border flex flex-col justify-between select-none relative active:scale-[0.98] ${
                    isSelected
                      ? "border-cyan-400 bg-cyan-500/15 shadow-[0_0_16px_rgba(6,182,212,0.25)] ring-1 ring-cyan-400/50"
                      : "border-[var(--mobile-border)] bg-[var(--mobile-bg-card)] hover:border-cyan-500/30"
                  }`}
                >
                  {/* Top: Icon + Mode Label (SEO/SEM/SER) + Power Switch / Curtain Switch / Type Badge */}
                  <div className="flex items-center justify-between gap-1.5 mb-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelectSensor(sensor.id);
                      }}
                      title="디바이스 선택 / 일괄 제어"
                      className={`p-2 rounded-xl ${colors.bg} ${colors.icon} flex-shrink-0 transition-transform active:scale-90 relative ${
                        isSelected ? 'ring-2 ring-cyan-400 shadow-[0_0_8px_rgba(6,182,212,0.5)]' : 'hover:opacity-80'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {isSelected && (
                        <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-cyan-400 text-black flex items-center justify-center text-[0.6rem] font-bold shadow-sm">
                          ✓
                        </span>
                      )}
                    </button>

                    {/* Right Group: Mode Label + Switch / Badge */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {isPlugOrRelay && (
                        <span
                          className="text-[0.62rem] font-medium px-1.5 py-0.5 rounded-md border"
                          style={{
                            background: 'var(--mobile-bg-sub)',
                            borderColor: 'var(--mobile-border)',
                            color: isAlwaysMode ? 'rgba(245, 158, 11, 0.85)' : 'var(--mobile-text-muted)',
                          }}
                        >
                          {modeLabel}
                        </span>
                      )}

                      {isControllableType && (!isPlugOrRelay || isNormalMode) ? (
                        <button
                          type="button"
                          disabled={!sensor.active || isAlwaysMode}
                          onClick={(e) => handleToggleSinglePower(sensor, !!isPowerOn, isAlwaysMode, e)}
                          title={!sensor.active ? '오프라인 (제어 불가)' : isAlwaysMode ? '상시전원 모드 (제어 불가)' : isPowerOn ? '전원 끄기' : '전원 켜기'}
                          className={`w-7 h-7 rounded-xl flex items-center justify-center transition-all duration-200 active:scale-90 flex-shrink-0 ${
                            !sensor.active || isAlwaysMode
                              ? 'bg-slate-800/80 border border-slate-600/70 text-slate-400/80 opacity-70 cursor-not-allowed'
                              : isPowerOn
                              ? 'bg-cyan-400 text-slate-950 border border-cyan-300 shadow-[0_0_14px_rgba(6,182,212,0.55)] hover:bg-cyan-300 font-bold'
                              : 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 hover:text-cyan-300 shadow-sm'
                          }`}
                        >
                          <PowerIcon className="w-3.5 h-3.5" />
                        </button>
                      ) : isCurtainType ? (
                        <button
                          type="button"
                          disabled={!sensor.active}
                          onClick={(e) => handleToggleCurtain(sensor, e)}
                          title={!sensor.active ? '오프라인 (제어 불가)' : '커튼 / 블라인드 제어'}
                          className={`w-7 h-7 rounded-xl flex items-center justify-center transition-all duration-200 active:scale-90 flex-shrink-0 shadow-sm ${
                            !sensor.active
                              ? 'opacity-30 cursor-not-allowed bg-white/5 border border-white/5 text-slate-600'
                              : 'bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25'
                          }`}
                        >
                          <ChevronsUpDownIcon className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="text-[0.68rem] font-bold px-1.5 py-0.5 rounded" style={{ background: 'var(--mobile-bg-sub)', color: 'var(--mobile-text-muted)' }}>
                          {getFriendlyDeviceTypeName(sensor.type)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Bottom Info: Left (Name + Online Dot / Alias) + Right (Value) */}
                  <div className="flex items-end justify-between gap-1 min-w-0">
                    {/* Left: Name with Online Dot & Appliance */}
                    {(() => {
                      const rawName = sensor.name || sensor.label || '디바이스';
                      const hasNickname = !!sensor.primary_appliance || (!!sensor.alias && sensor.alias !== rawName) || (!!sensor.label && sensor.label !== rawName);
                      const mainTitle = hasNickname ? (sensor.primary_appliance || sensor.alias || sensor.label) : rawName;
                      const subTitle = hasNickname ? rawName : getFriendlyDeviceTypeName(sensor.type);

                      return (
                        <div className="min-w-0 flex-1">
                          <div className="text-[0.85rem] font-semibold truncate leading-tight flex items-center gap-1.5" style={{ color: 'var(--mobile-text-primary)' }}>
                            <span className="truncate">{mainTitle}</span>
                            {sensor.active ? (
                              <motion.span
                                className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-emerald-400 inline-block"
                                animate={{ opacity: [1, 0.4, 1] }}
                                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                              />
                            ) : (
                              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-slate-600 inline-block" />
                            )}
                          </div>
                          <div className={`text-[0.68rem] truncate mt-0.5 ${hasNickname ? 'text-slate-400 font-mono' : ''}`} style={{ color: hasNickname ? undefined : 'var(--mobile-text-muted)' }}>
                            {subTitle}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Right: Telemetry Value */}
                    <div className="flex-shrink-0 text-right">
                      {(sensor.type === 'SEO' || sensor.type === 'SEM' || sensor.type === 'SER') && powerVal !== undefined && (
                        <span className="text-[0.85rem] font-mono font-bold text-violet-400">
                          {Number(powerVal).toFixed(1)}W
                        </span>
                      )}
                      {sensor.type === 'SEL' && lightVal !== undefined && (
                        <span className={`text-[0.85rem] font-bold ${lightVal === '0' || lightVal === 'false' ? 'text-slate-500' : 'text-amber-400'}`}>
                          {lightVal === '0' || lightVal === 'false' ? '꺼짐' : '켜짐'}
                        </span>
                      )}
                      {sensor.type === 'SES' && tempVal !== undefined && (
                        <div className="flex items-center justify-end gap-1 text-[0.72rem] font-mono">
                          <span className="font-bold text-rose-400">{Number(tempVal).toFixed(1)}°C</span>
                          <span className="text-slate-600">|</span>
                          <span className="font-bold text-cyan-400">{Number(humVal).toFixed(0)}%</span>
                        </div>
                      )}
                      {sensor.type === 'RADAR' && (
                        <div className={`text-[0.78rem] font-bold ${presVal === 'true' ? 'text-rose-400' : 'text-slate-500'}`}>
                          {presVal === 'true' ? '재실' : '부재'}
                        </div>
                      )}
                      {(sensor.type?.toUpperCase() === 'SEV' || sensor.type?.toUpperCase() === 'HERV') && (
                        (() => {
                          const status = formatErvStatus(ervVal);
                          if (!status) {
                            return (
                              <span className="text-[0.82rem] font-bold text-slate-500">
                                -
                              </span>
                            );
                          }
                          return (
                            <span className={`text-[0.82rem] font-bold ${status.color}`}>
                              {status.text}
                            </span>
                          );
                        })()
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
